import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Minus, Plus, Trash2 } from 'lucide-react';
import type { CountSessionDetailDto, CountSessionDto, LocationDto, PurchaseOrderDetailDto, PurchaseOrderDto, TransferDetailDto, TransferDto } from '@thebb/shared';
import { get, post } from '../../api';
import { Camera } from '../../scanner/Camera';
import { beepError, beepSuccess } from '../../scanner/feedback';
import { Finished, Toast, TopBar, errText, norm, useWarehouse } from './Mobile';

/** Resolves a scanned code to a location in the warehouse: its barcode or its code. */
async function findLocation(warehouseId: string, code: string): Promise<LocationDto | null> {
  const r = await get<{ items: LocationDto[] }>(`/locations?warehouseId=${warehouseId}&perPage=20&q=${encodeURIComponent(code)}`);
  const c = norm(code);
  return r.items.find((l) => norm(l.barcode) === c || norm(l.code) === c) ?? null;
}

// --- receive ------------------------------------------------------------------------------------------

/** Open purchase orders and transfers bound for the warehouse, or scan the document number. */
export function MobileReceiveList() {
  const navigate = useNavigate();
  const { warehouseId } = useWarehouse();
  const [msg, setMsg] = useState<string | null>(null);
  const pos = useQuery({ queryKey: ['m-pos', warehouseId], queryFn: () => get<{ items: PurchaseOrderDto[] }>(`/purchase-orders?open=true&perPage=100${warehouseId ? `&warehouseId=${warehouseId}` : ''}`) });
  const trs = useQuery({ queryKey: ['m-transfers'], queryFn: () => get<{ items: TransferDto[] }>('/transfers?open=true&perPage=100') });
  const poList = (pos.data?.items ?? []).filter((p) => p.status !== 'draft');
  const trList = (trs.data?.items ?? []).filter((t) => !warehouseId || t.toWarehouseId === warehouseId);
  const onDetect = async (raw: string) => {
    const r = await get<{ kind: string | null; id: string | null }>(`/inbound/scan?code=${encodeURIComponent(norm(raw))}`);
    if (r.kind === 'purchase_order' || r.kind === 'transfer') {
      beepSuccess();
      void navigate({ to: '/m/receive/$kind/$id', params: { kind: r.kind === 'purchase_order' ? 'po' : 'tr', id: r.id! } });
      return;
    }
    beepError();
    setMsg(`Ingen indkøbsordre eller overførsel ${raw}`);
  };
  return (
    <>
      <TopBar title="Modtag varer" back />
      <Camera active compact hint="Scan indkøbsordre eller overførsel (PO- / TR-)" onDetect={(c) => void onDetect(c)} />
      {msg && <Toast msg={msg} tone="err" />}
      <div className="space-y-4 p-3">
        <section>
          <h2 className="mb-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-muted">Indkøbsordrer</h2>
          <ul className="divide-y divide-line overflow-hidden rounded-2xl bg-white shadow-card">
            {poList.map((p) => (
              <li key={p.id}><Link to="/m/receive/$kind/$id" params={{ kind: 'po', id: p.id }} className="flex items-center gap-3 px-4 py-3 active:bg-primary-50"><div className="min-w-0 flex-1"><div className="font-semibold">{p.number} · {p.supplierName}</div><div className="text-xs text-muted">{p.quantityReceived}/{p.quantityOrdered} stk{p.expectedAt ? ` · forventet ${p.expectedAt}` : ''}</div></div><ChevronRight size={18} className="text-muted" /></Link></li>
            ))}
            {poList.length === 0 && <li className="px-4 py-6 text-center text-sm text-muted">Ingen åbne indkøbsordrer.</li>}
          </ul>
        </section>
        <section>
          <h2 className="mb-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-muted">Overførsler</h2>
          <ul className="divide-y divide-line overflow-hidden rounded-2xl bg-white shadow-card">
            {trList.map((t) => (
              <li key={t.id}><Link to="/m/receive/$kind/$id" params={{ kind: 'tr', id: t.id }} className="flex items-center gap-3 px-4 py-3 active:bg-primary-50"><div className="min-w-0 flex-1"><div className="font-semibold">{t.number} · {t.fromWarehouseCode} → {t.toWarehouseCode}</div><div className="text-xs text-muted">{t.quantityRequested} stk{t.requestedPallets ? ` · ${t.requestedPallets}` : ''}</div></div><ChevronRight size={18} className="text-muted" /></Link></li>
            ))}
            {trList.length === 0 && <li className="px-4 py-6 text-center text-sm text-muted">Ingen åbne overførsler.</li>}
          </ul>
        </section>
      </div>
    </>
  );
}

type Entry = { lineId: string; sku: string; quantity: number; locationId: string; locationCode: string };

/** Scan item, count, scan the shelf: the arrival builds up as entries and is posted as one receipt. */
export function MobileReceive() {
  const { kind, id } = useParams({ from: '/m/receive/$kind/$id' });
  const qc = useQueryClient();
  const path = kind === 'po' ? 'purchase-orders' : 'transfers';
  const doc = useQuery({ queryKey: ['m-receive', kind, id], queryFn: () => get<PurchaseOrderDetailDto | TransferDetailDto>(`/${path}/${id}`) });
  const storeKey = `m.receive.${id}`;
  const [entries, setEntries] = useState<Entry[]>(() => { try { return JSON.parse(localStorage.getItem(storeKey) ?? '[]') as Entry[]; } catch { return []; } });
  const save = (e: Entry[]) => { setEntries(e); try { localStorage.setItem(storeKey, JSON.stringify(e)); } catch { /* ignore */ } };
  const [current, setCurrent] = useState<{ lineId: string; sku: string; title: string; qty: number } | null>(null);
  const [msg, setMsg] = useState<{ text: string; tone: 'ok' | 'err' } | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const d = doc.data;
  const warehouseId = d ? ('warehouseId' in d ? d.warehouseId : d.toWarehouseId) : '';
  const lines = useMemo(() => (d ? d.lines.map((l) => ({ id: l.id, sku: l.sku, title: l.title, barcodes: l.barcodes, expected: 'quantityOrdered' in l ? l.quantityOrdered : Math.max(l.quantityShipped, l.quantityRequested), received: l.quantityReceived })) : []), [d]);
  const pending = (lineId: string) => entries.filter((e) => e.lineId === lineId).reduce((t, e) => t + e.quantity, 0);
  const post_ = useMutation({
    mutationFn: () => {
      const merged = new Map<string, { lineId: string; quantity: number; locationId: string }>();
      for (const e of entries) {
        const k = `${e.lineId}|${e.locationId}`;
        const m = merged.get(k);
        if (m) m.quantity += e.quantity;
        else merged.set(k, { lineId: e.lineId, quantity: e.quantity, locationId: e.locationId });
      }
      return post<{ receiptNumber: string; releasedFulfilments: string[] }>(`/${path}/${id}/receive`, { lines: [...merged.values()] });
    },
    onSuccess: (r) => { beepSuccess(); save([]); void qc.invalidateQueries({ queryKey: ['m-receive', kind, id] }); setDone(`${r.receiptNumber} bogført${r.releasedFulfilments.length ? `. Restordrer frigivet: ${r.releasedFulfilments.join(', ')}` : ''}`); },
    onError: (e) => { beepError(); setMsg({ text: errText(e), tone: 'err' }); },
  });
  const onDetect = async (raw: string) => {
    const code = norm(raw);
    if (!current) {
      const line = lines.find((l) => norm(l.sku) === code || l.barcodes.some((b) => norm(b) === code));
      if (!line) { beepError(); setMsg({ text: `${raw} er ikke på ${d?.number ?? 'dokumentet'}`, tone: 'err' }); return; }
      beepSuccess();
      setMsg(null);
      setCurrent({ lineId: line.id, sku: line.sku, title: line.title, qty: 1 });
      return;
    }
    // Same item again counts one more; anything else is the shelf.
    if (norm(current.sku) === code || lines.find((l) => l.id === current.lineId)?.barcodes.some((b) => norm(b) === code)) {
      beepSuccess();
      setCurrent({ ...current, qty: current.qty + 1 });
      return;
    }
    const loc = await findLocation(warehouseId, raw);
    if (!loc) { beepError(); setMsg({ text: `Ukendt lokation ${raw}`, tone: 'err' }); return; }
    beepSuccess();
    save([...entries, { lineId: current.lineId, sku: current.sku, quantity: current.qty, locationId: loc.id, locationCode: loc.code }]);
    setMsg({ text: `${current.qty} × ${current.sku} → ${loc.code}`, tone: 'ok' });
    setCurrent(null);
  };
  if (!d) return <TopBar title="Modtag" back />;
  if (done) return (<><TopBar title={d.number} back /><Finished title="Modtagelse bogført"><div className="text-sm text-muted">{done}</div><button className="btn" onClick={() => setDone(null)}>Modtag mere på {d.number}</button><Link to="/m/receive" className="btn btn-primary">Til modtagelse</Link></Finished></>);
  const total = entries.reduce((t, e) => t + e.quantity, 0);
  return (
    <>
      <TopBar title={`Modtag ${d.number}`} back />
      <Camera active={!post_.isPending} compact hint={current ? `Scan hylden til ${current.sku} (eller varen igen for +1)` : 'Scan varen'} onDetect={(c) => void onDetect(c)} />
      {msg && <Toast msg={msg.text} tone={msg.tone} />}
      <div className="space-y-3 p-3">
        {current && (
          <div className="rounded-2xl bg-white p-4 shadow-card">
            <div className="font-semibold">{current.sku}</div>
            <div className="text-sm text-muted">{current.title}</div>
            <div className="mt-3 flex items-center justify-center gap-4">
              <button className="rounded-full bg-canvas p-3" onClick={() => setCurrent({ ...current, qty: Math.max(1, current.qty - 1) })}><Minus /></button>
              <input className="input w-24 text-center text-2xl font-bold" type="number" inputMode="numeric" value={current.qty} onChange={(e) => setCurrent({ ...current, qty: Math.max(1, Number(e.target.value) || 1) })} />
              <button className="rounded-full bg-canvas p-3" onClick={() => setCurrent({ ...current, qty: current.qty + 1 })}><Plus /></button>
            </div>
            <div className="mt-2 text-center text-xs text-muted">Scan hylden, varen lægges på</div>
            <button className="mt-2 w-full text-sm text-muted underline" onClick={() => setCurrent(null)}>Fortryd</button>
          </div>
        )}
        {entries.length > 0 && (
          <section className="rounded-2xl bg-white shadow-card">
            <div className="flex items-center justify-between px-4 pt-3"><div className="text-xs font-semibold uppercase tracking-wide text-muted">Klar til bogføring</div><span className="text-sm font-semibold">{total} stk.</span></div>
            <ul className="divide-y divide-line">
              {entries.map((e, i) => (
                <li key={i} className="flex items-center gap-2 px-4 py-2 text-sm"><span className="flex-1">{e.quantity} × {e.sku} → <b>{e.locationCode}</b></span><button className="p-1 text-muted" onClick={() => save(entries.filter((_, j) => j !== i))} aria-label="Fjern"><Trash2 size={16} /></button></li>
              ))}
            </ul>
            <div className="p-3"><button className="btn btn-primary w-full py-3 text-base" disabled={post_.isPending} onClick={() => post_.mutate()}>Bogfør modtagelse</button></div>
          </section>
        )}
        <section>
          <h2 className="mb-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-muted">Linjer</h2>
          <ul className="divide-y divide-line overflow-hidden rounded-2xl bg-white shadow-card">
            {lines.map((l) => {
              const p = pending(l.id);
              const left = l.expected - l.received - p;
              return (
                <li key={l.id} className={`flex items-center gap-3 px-4 py-2.5 ${left <= 0 ? 'opacity-50' : ''}`}>
                  <div className="min-w-0 flex-1"><div className="text-sm font-semibold">{l.sku}</div><div className="truncate text-xs text-muted">{l.title}</div></div>
                  <div className="text-right text-sm tabular-nums">{l.received + p}/{l.expected}</div>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </>
  );
}

// --- count ----------------------------------------------------------------------------------------------

export function MobileCountList() {
  const { warehouseId } = useWarehouse();
  const list = useQuery({ queryKey: ['m-counts'], queryFn: () => get<{ items: CountSessionDto[] } | CountSessionDto[]>('/counts?status=open&perPage=50') });
  const items = (Array.isArray(list.data) ? list.data : (list.data?.items ?? [])).filter((c) => c.status === 'open' && (!warehouseId || c.warehouseId === warehouseId));
  return (
    <>
      <TopBar title="Optælling" back />
      <div className="space-y-3 p-3">
        <ul className="divide-y divide-line overflow-hidden rounded-2xl bg-white shadow-card">
          {items.map((c) => (
            <li key={c.id}><Link to="/m/count/$id" params={{ id: c.id }} className="flex items-center gap-3 px-4 py-3 active:bg-primary-50"><div className="min-w-0 flex-1"><div className="font-semibold">{c.name}</div><div className="text-xs text-muted">{c.warehouseCode} · {c.countedLines}/{c.lineCount} linjer talt</div></div><ChevronRight size={18} className="text-muted" /></Link></li>
          ))}
          {items.length === 0 && <li className="px-4 py-6 text-center text-sm text-muted">{list.isLoading ? 'Henter…' : 'Ingen åbne optællinger. De oprettes i back office under Optælling.'}</li>}
        </ul>
      </div>
    </>
  );
}

/** Scan a shelf, then scan or tap the items on it; counts are saved per shelf. */
export function MobileCount() {
  const { id } = useParams({ from: '/m/count/$id' });
  const qc = useQueryClient();
  const s = useQuery({ queryKey: ['m-count', id], queryFn: () => get<CountSessionDetailDto>(`/counts/${id}`) });
  const [loc, setLoc] = useState<{ id: string; code: string } | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [extra, setExtra] = useState<Record<string, number>>({});
  const [msg, setMsg] = useState<{ text: string; tone: 'ok' | 'err' } | null>(null);
  const d = s.data;
  const here = useMemo(() => (d && loc ? d.lines.filter((l) => l.locationId === loc.id) : []), [d, loc]);
  const saveIt = useMutation({
    mutationFn: () => post<CountSessionDetailDto>(`/counts/${id}/enter`, {
      lines: here.map((l) => ({ lineId: l.id, counted: counts[l.id] ?? 0 })),
      add: Object.entries(extra).map(([sku, counted]) => ({ sku, locationCode: loc!.code, counted })),
    }),
    onSuccess: (r) => { beepSuccess(); qc.setQueryData(['m-count', id], r); setMsg({ text: `${loc?.code} gemt`, tone: 'ok' }); setLoc(null); setCounts({}); setExtra({}); },
    onError: (e) => { beepError(); setMsg({ text: errText(e), tone: 'err' }); },
  });
  const onDetect = async (raw: string) => {
    if (!d) return;
    const code = norm(raw);
    if (!loc) {
      const line = d.lines.find((l) => norm(l.locationBarcode) === code || norm(l.locationCode) === code);
      const found = line ? { id: line.locationId, code: line.locationCode } : await findLocation(d.warehouseId, raw);
      if (!found) { beepError(); setMsg({ text: `Ukendt lokation ${raw}`, tone: 'err' }); return; }
      beepSuccess();
      setMsg(null);
      setLoc({ id: found.id, code: found.code });
      return;
    }
    const line = here.find((l) => norm(l.sku) === code || l.barcodes.some((b) => norm(b) === code));
    if (line) { beepSuccess(); setCounts((c) => ({ ...c, [line.id]: (c[line.id] ?? 0) + 1 })); return; }
    const other = d.lines.find((l) => l.locationId !== loc.id && (norm(l.sku) === code || l.barcodes.some((b) => norm(b) === code)));
    beepSuccess();
    const sku = other?.sku ?? raw.trim();
    setExtra((x) => ({ ...x, [sku]: (x[sku] ?? 0) + 1 }));
    setMsg({ text: `${sku} er ikke forventet på ${loc.code}; tælles som fundet her`, tone: 'err' });
  };
  if (!d) return <TopBar title="Optælling" back />;
  const counted = d.lines.filter((l) => l.counted !== null).length;
  return (
    <>
      <TopBar title={d.name} back right={<span className="text-sm">{counted}/{d.lines.length}</span>} />
      <Camera active={!saveIt.isPending} compact hint={loc ? `Scan varerne på ${loc.code}` : 'Scan hylden'} onDetect={(c) => void onDetect(c)} />
      {msg && <Toast msg={msg.text} tone={msg.tone} />}
      <div className="space-y-3 p-3">
        {loc ? (
          <section className="rounded-2xl bg-white shadow-card">
            <div className="flex items-center justify-between px-4 pt-3"><div className="font-semibold">Hylde {loc.code}</div><button className="text-sm text-muted underline" onClick={() => { setLoc(null); setCounts({}); setExtra({}); }}>Skift hylde</button></div>
            <ul className="divide-y divide-line">
              {here.map((l) => (
                <li key={l.id} className="flex items-center gap-2 px-4 py-2.5">
                  <div className="min-w-0 flex-1"><div className="text-sm font-semibold">{l.sku}</div><div className="truncate text-xs text-muted">{l.productTitle}{l.counted !== null ? ` · talt før: ${l.counted}` : ''}</div></div>
                  <button className="rounded-full bg-canvas p-2" onClick={() => setCounts((c) => ({ ...c, [l.id]: Math.max(0, (c[l.id] ?? l.counted ?? 0) - 1) }))}><Minus size={16} /></button>
                  <input className="input w-16 text-center font-bold" type="number" inputMode="numeric" value={counts[l.id] ?? ''} placeholder="?" onChange={(e) => setCounts((c) => ({ ...c, [l.id]: Math.max(0, Number(e.target.value) || 0) }))} />
                  <button className="rounded-full bg-canvas p-2" onClick={() => setCounts((c) => ({ ...c, [l.id]: (c[l.id] ?? l.counted ?? 0) + 1 }))}><Plus size={16} /></button>
                </li>
              ))}
              {Object.entries(extra).map(([sku, n]) => <li key={sku} className="flex items-center gap-2 px-4 py-2.5 text-sm"><span className="flex-1">{sku} <span className="text-muted">(ikke forventet)</span></span><b>{n}</b></li>)}
              {here.length === 0 && Object.keys(extra).length === 0 && <li className="px-4 py-4 text-center text-sm text-muted">Intet forventet på hylden. Scan det, der står der.</li>}
            </ul>
            <div className="p-3"><button className="btn btn-primary w-full py-3 text-base" disabled={saveIt.isPending} onClick={() => saveIt.mutate()}>Gem {loc.code} (ikke scannede = 0)</button></div>
          </section>
        ) : (
          <div className="rounded-2xl bg-white p-4 text-center text-sm text-muted shadow-card">Scan hyldens stregkode. Varer, der ikke scannes, gemmes som 0 på den hylde.</div>
        )}
      </div>
    </>
  );
}
